import React from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Loader2, Trash2, UserPlus, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export function UsersDialog({ open, onOpenChange, currentUserId }) {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [form, setForm] = React.useState({ email: "", password: "", name: "", role: "user" });
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/auth/users`)
      .then((r) => setUsers(r.data))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    if (open) load();
  }, [open, load]);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await axios.post(`${API}/auth/users`, form);
      setForm({ email: "", password: "", name: "", role: "user" });
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo crear el usuario");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este usuario? Esta acción no se puede deshacer.")) return;
    await axios.delete(`${API}/auth/users/${id}`).catch(() => {});
    load();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="users-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#E2333F]" /> Usuarios
          </DialogTitle>
          <DialogDescription>Crea y administra el acceso al dashboard.</DialogDescription>
        </DialogHeader>

        <form onSubmit={add} className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-sm p-3">
          <input required type="email" placeholder="Correo" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email"
            className="col-span-2 h-9 px-2 border border-slate-200 rounded-sm text-sm" />
          <input required type="text" placeholder="Nombre" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name"
            className="h-9 px-2 border border-slate-200 rounded-sm text-sm" />
          <input required type="text" placeholder="Contraseña" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password"
            className="h-9 px-2 border border-slate-200 rounded-sm text-sm" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="h-9 px-2 border border-slate-200 rounded-sm text-sm bg-white" data-testid="new-user-role">
            <option value="user">Usuario</option>
            <option value="admin">Administrador</option>
          </select>
          <button type="submit" disabled={saving} data-testid="new-user-submit"
            className="h-9 rounded-sm bg-[#E2333F] text-white text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-[#c92a36] disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Agregar
          </button>
          {error && <div className="col-span-2 text-xs text-red-600">{error}</div>}
        </form>

        <div className="mt-3 max-h-[300px] overflow-y-auto border border-slate-200 rounded-sm">
          {loading ? (
            <div className="p-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : (
            users.map((u) => (
              <div key={u.id} className="flex items-center justify-between px-3 py-2 border-b border-slate-100 last:border-b-0" data-testid={`user-row-${u.email}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{u.name || u.email}</div>
                  <div className="text-xs text-slate-400 truncate">{u.email}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${u.role === "admin" ? "bg-[#E2333F]/10 text-[#E2333F]" : "bg-slate-100 text-slate-500"}`}>
                    {u.role}
                  </span>
                  {u.id !== currentUserId && (
                    <button onClick={() => remove(u.id)} data-testid={`delete-user-${u.email}`} className="text-slate-300 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
